import { clsx } from 'clsx'

import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon, Icon } from '@tribeplatform/react-ui-kit/Icon'
import { SortableList } from '@tribeplatform/react-ui-kit/SortableList'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { T } from '../../i18n/index.js'

export enum FieldSortableItemVariant {
  SWITCH = 'switch',
  EYE = 'eye',
  NONE = 'none',
  DOTS = 'dots',
}
interface Props {
  id: string
  label: string
  icon: JSX.Element
  enabled: boolean
  hasArrow?: boolean
  disabled?: boolean
  variant?: FieldSortableItemVariant
  isPrivate?: boolean
  isLocked?: boolean
  draggable?: boolean
  tooltip?: string
  tooltipIcon?: JSX.Element
  dropDownItems?: JSX.Element
  onClick?: (id: string) => void
  onToggle: (id: string) => void
  onActivate?: (id: string) => void
}
export const FieldSortableItem = ({
  id,
  label,
  icon,
  enabled,
  hasArrow = true,
  disabled,
  draggable = true,
  variant = FieldSortableItemVariant.EYE,
  isPrivate,
  isLocked,
  tooltip,
  tooltipIcon,
  dropDownItems,
  onClick,
  onToggle,
  onActivate,
}: Props) => {
  return (
    <div
      className={clsx(
        'flex-1 h-11 border border-line-subdued rounded-base px-1 py-1.5 bg-surface flex space-s-2 items-center truncate',
        !disabled && 'hover:bg-surface-hovered cursor-pointer',
        !draggable && 'pl-2',
      )}
      onClick={() => {
        if (onClick && !disabled) {
          onClick(id)
        }
      }}
      onMouseOver={() => {
        if (onActivate && !disabled) {
          onActivate(id)
        }
      }}
      onFocus={() => {
        if (onActivate && !disabled) {
          onActivate(id)
        }
      }}
      onMouseOut={() => {
        if (onActivate && !disabled) {
          onActivate('')
        }
      }}
      onBlur={() => {
        if (onActivate && !disabled) {
          onActivate('')
        }
      }}
    >
      {draggable && <SortableList.Handle disabled={disabled} />}
      {icon && (
        <Icon
          className={clsx(
            disabled ? 'text-content-disabled' : 'text-content-subdued',
            'w-5 h-5 shrink-0',
          )}
        >
          {icon}
        </Icon>
      )}
      <span
        className={clsx(
          'flex-1 text-sm truncate',
          disabled ? 'text-content-disabled' : 'text-content-subdued',
        )}
      >
        {isPrivate && !isLocked && (
          <Tooltip>
            <Tooltip.Trigger className="truncate inline">
              {label} <SvgIcon inline name="eye-off" />
            </Tooltip.Trigger>
            <Tooltip.Panel>
              <T
                defaultMessage="This field is hidden from non-admins"
                id="FieldSortableItem.PrivateField"
              />
            </Tooltip.Panel>
          </Tooltip>
        )}
        {isLocked && (
          <Tooltip>
            <Tooltip.Trigger className="truncate inline">
              {label} <SvgIcon inline name="lock" />
            </Tooltip.Trigger>
            <Tooltip.Panel>
              <T
                defaultMessage="You cannot archive this field"
                id="FieldSortableItem.LockedField"
              />
            </Tooltip.Panel>
          </Tooltip>
        )}
        {!!tooltip && (
          <Tooltip>
            <Tooltip.Trigger className="truncate">
              {label} {tooltipIcon}
            </Tooltip.Trigger>
            <Tooltip.Panel>{tooltip}</Tooltip.Panel>
          </Tooltip>
        )}
        {!isPrivate && !tooltip && !isLocked && label}
      </span>
      <div className="flex space-s-1 items-center px-1">
        {variant === FieldSortableItemVariant.EYE && (
          <Button
            variant="tertiaryNeutral"
            className="block rounded-sm bg-transparent hover:bg-skeleton p-1.5"
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              onToggle(id)
            }}
            disabled={disabled}
          >
            <SvgIcon name={enabled ? 'eye' : 'eye-off'} />
          </Button>
        )}
        {variant === FieldSortableItemVariant.SWITCH && (
          <div
            onClick={e => {
              e.stopPropagation()
            }}
          >
            <Toggle
              size="sm"
              checked={enabled}
              onChange={() => {
                onToggle(id)
              }}
              disabled={disabled}
            />
          </div>
        )}
        {variant === FieldSortableItemVariant.DOTS && !!dropDownItems && (
          <div
            onClick={e => {
              e.stopPropagation()
            }}
          >
            <Dropdown placement="bottom-end">
              <Dropdown.ButtonMinimal className="p-2 bg-transparent" />
              {dropDownItems}
            </Dropdown>
          </div>
        )}
        {hasArrow && (
          <SvgIcon
            className="w-4 h-4 shrink-0 text-content-subdued rtl:-scale-x-100"
            name="chevron-right"
          />
        )}
      </div>
    </div>
  )
}
